import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

// Types
import { GoalItem } from 'Shared/constants/types';
// import { WorkspaceComment } from 'Shared/types/WorkspaceComment.type';

// Actions
import * as commentActions from 'Shared/actions/Comments.actions';

// Hooks
import { useGoal } from 'Shared/utils/hooks/use-goal';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Components
import { WpButton } from 'Wpds/components/button';
import CommentListItem from 'Shared/components/Comments/CommentListItem';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';
// import CommentsLoading from 'Shared/components/CommentsLoading/CommentsLoading';

// Styles
import './GoalComments.scss';

export enum SortDirection {
  OLDEST_FIRST,
  NEWEST_FIRST,
}

type Props = {
  goal: GoalItem;
  truncateLimit?: number;
  sort?: SortDirection;
};
const GoalComments = ({ goal, truncateLimit = 4, sort = SortDirection.OLDEST_FIRST }: Props) => {
  const { tr } = useTranslation();
  const dispatch = useDispatch();

  const { comments } = useGoal({ slug: goal.slug });

  const [viewFullList, setViewFullList] = useState(false);

  const hasComments = comments.length > 0;

  const handleDeleteComment = useCallback(
    slug => {
      return dispatch(commentActions.deleteComment(slug));
    },
    [commentActions]
  );

  const handleUpdateComment = useCallback(
    data => {
      return dispatch(commentActions.updateComment(data));
    },
    [commentActions]
  );

  // if (isFetchingGoal) return <CommentsLoading countToRender={2} />;

  const truncateListTosize = truncateLimit;
  const remainingSize = viewFullList ? 0 : comments.length - truncateListTosize;
  const hasMoreComments = remainingSize > 0;

  const sortedComments = sort === SortDirection.NEWEST_FIRST ? comments.reverse() : comments;

  const listToRender =
    viewFullList || !hasMoreComments
      ? sortedComments
      : sort === SortDirection.NEWEST_FIRST
      ? sortedComments.slice(0, truncateListTosize)
      : sortedComments.slice(sortedComments.length - truncateListTosize, sortedComments.length);

  const baseClassName = 'GoalComments';

  return (
    <div className={baseClassName}>
      {!hasComments && <EmptyStateContainer subTitle={tr('perform.goals.comments.empty_state')} />}
      {hasMoreComments && sort === SortDirection.OLDEST_FIRST && (
        <div className={`${baseClassName}__see-more ${baseClassName}__see-more--top`}>
          <WpButton variant="tertiary" size="sm" onClick={() => setViewFullList(true)}>
            {remainingSize > 1
              ? tr('perform.comments.show_more_plural', String(remainingSize))
              : tr('perform.comments.show_more_single', String(remainingSize))}
          </WpButton>
        </div>
      )}
      <TransitionGroup>
        {listToRender.map(comment => (
          <CSSTransition key={`comment-${comment.uuid}`} timeout={500} classNames={`${baseClassName}__animation`}>
            <CommentListItem
              comment={comment}
              handleDeleteComment={handleDeleteComment}
              handleUpdateComment={handleUpdateComment}
              workspaceId={goal.workspaceSlug}
            />
          </CSSTransition>
        ))}
      </TransitionGroup>
      {hasMoreComments && sort === SortDirection.NEWEST_FIRST && (
        <div className={`${baseClassName}__see-more ${baseClassName}__see-more--bottom`}>
          <WpButton variant="tertiary" size="sm" onClick={() => setViewFullList(true)}>
            {remainingSize > 1
              ? tr('perform.comments.show_more_plural', String(remainingSize))
              : tr('perform.comments.show_more_single', String(remainingSize))}
          </WpButton>
        </div>
      )}
    </div>
  );
};

export default GoalComments;
